import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";

import "./HomeScreen.css";
import { Card } from "../../components/card/Card";
import { useTranslation } from "react-i18next";
import { selectProducts } from "../../../store/selectors/ProductSelector";
import { selectOperations } from "../../../store/selectors/OperationsSelector";
import { isDateInCurrentMonth } from "../../../utils/GlobalFunctions";
import { OperationType } from "../../../entity/Operation";

import { OrderStateComponent } from "./components/stats/order/OrderStateComponent";
import { ConfirmationOrder } from "./components/stats/confirmation/ConfirmationOrder";

interface Props {}

export const HomeScreen = () => {
  const products = useSelector(selectProducts);
  const operations = useSelector(selectOperations);

  const { t } = useTranslation();

  const [sumOperations, setSumOperations] = useState(0);
  const [nbOperations, setNbOperations] = useState(0);
  const [sumCurrentMonth, setSumCurrentMonth] = useState(0);
  const [nbOperationThisMonth, setNbOperationThisMonth] = useState(0);
  const [stockValue, setStockValue] = useState(0);
  const [nbStock, setNbStock] = useState(0);
  const [sumBenefits, setSumBenefits] = useState(0);

  useEffect(() => {
    if (operations.length > 0) {
      let totalSum = 0;
      let totalBenefits = 0;
      let totalCount = 0;
      let monthSum = 0;
      let monthCount = 0;

      operations.forEach((operation) => {
        if (
          operation.type === OperationType.TRANSFER_IN ||
          operation.type === OperationType.SELL
        ) {
          totalSum += operation.amount;
          totalCount++;

          if (isDateInCurrentMonth(operation.date)) {
            monthSum += operation.amount;
            monthCount++;
          }

          totalBenefits += operation.amount;
        } else {
          totalBenefits -= operation.amount;
        }
      });

      setSumOperations(totalSum);
      setNbOperations(totalCount);
      setSumCurrentMonth(monthSum);
      setNbOperationThisMonth(monthCount);
      setSumBenefits(totalBenefits);
    }
  }, [operations]);

  useEffect(() => {
    if (products.length > 0) {
      const totalStockValue = products.reduce((sum, product) => {
        let total = 0;

        if (product.productCost) {
          total = product.productCost * product.quantity;
        }

        return sum + total;
      }, 0);

      const totalStockQuantity = products.reduce((sum, product) => {
        return sum + product.quantity;
      }, 0);

      setStockValue(totalStockValue);
      setNbStock(totalStockQuantity);
    }
  }, [products]);

  return (
    <div className="hms-container">
      {/* Stats Cards */}
      <div className="hms-header">
        <div className="hms-card-wrapper">
          <Card
            className="mc-card-info"
            title={t("stats.total_revenue")}
            value={sumOperations}
            description={`${nbOperations} ${t("stats.txt_operations")}`}
          />
        </div>

        <div className="hms-card-wrapper">
          <Card
            className="mc-card-info"
            title={t("stats.total_revenue_current_month")}
            value={sumCurrentMonth}
            description={`${nbOperationThisMonth} ${t("stats.txt_operations")}`}
          />
        </div>

        <div className="hms-card-wrapper">
          <Card
            className="mc-card-info"
            title={t("stats.stock_value")}
            value={stockValue}
            description={`${nbStock} ${t("stats.txt_products")}`}
          />
        </div>

        <div className="hms-card-wrapper">
          <Card
            className="mc-card-success"
            title={t("stats.txt_benefits")}
            value={sumBenefits}
          />
        </div>
      </div>

      {/* Charts & Stats */}
      <div className="hms-stats-container">
        <div className="hms-stats-grid">
          <div className="hms-widget-card">
            <OrderStateComponent />
          </div>

          <div className="hms-widget-card">
            <ConfirmationOrder />
          </div>
        </div>
      </div>
    </div>
  );
};
